/**
 * Unified Listings management: Refresh listings, Drive folder config, EPG/TV settings,
 * streaming settings, ingest history, and accessibility upload audit list.
 */
import { useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { supabase } from '@/lib/supabase'
import { FREEVIEW_EPG_FEED_NAME } from '@/lib/listingsSyncUtils'
import { useListingsManagementDashboard } from '@/hooks/useListingsManagementDashboard'
import { useN8nIngestRuns } from '@/hooks/useN8nIngestRuns'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'
import BroadcasterAccessibilityUpload from '@/components/BroadcasterAccessibilityUpload'
import { ListingsRefreshSection } from '@/components/listings-management/ListingsRefreshSection'
import { FolderConfigSection } from '@/components/listings-management/FolderConfigSection'
import { EpgSettingsSection } from '@/components/listings-management/EpgSettingsSection'
import { StreamingSettingsSection } from '@/components/listings-management/StreamingSettingsSection'
import { IngestHistorySection } from '@/components/listings-management/IngestHistorySection'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

export default function AdminListingsManagementPage() {
  const { user, session } = useAuth()
  const isAdmin = useAdmin()

  const { data: feed } = useQuery({
    queryKey: ['external-feed', FREEVIEW_EPG_FEED_NAME],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('external_feeds')
        .select('id, name')
        .eq('name', FREEVIEW_EPG_FEED_NAME)
        .eq('enabled', true)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user && isAdmin,
  })

  const { data: dashboard } = useListingsManagementDashboard(!!user && isAdmin)
  const {
    data: ingestRuns = [],
    isLoading: ingestRunsLoading,
    isError: ingestRunsError,
  } = useN8nIngestRuns(!!user && isAdmin)

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (!hash) return
    const el = document.getElementById(hash)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        Listings management
      </h1>
      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        EPG sync, Google Drive ingest folders, accessibility overlays, and streaming catalogue — one place for admins.{' '}
        <Link className="underline" style={{ color: 'var(--color-focus)' }} to={`${ADMIN_PORTAL_PATH}/listings-channels`}>
          Channel control
        </Link>
        {' · '}
        <Link className="underline" style={{ color: 'var(--color-focus)' }} to={`${ADMIN_PORTAL_PATH}/listings-health`}>
          Listings health
        </Link>
      </p>

      <nav className="mb-8 flex flex-wrap gap-2 text-sm" aria-label="Page sections">
        {[
          ['refresh', 'Refresh'],
          ['folders', 'Folders'],
          ['epg', 'EPG & TV'],
          ['streaming', 'Streaming'],
          ['history', 'History'],
          ['uploads', 'Uploads'],
        ].map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className="px-3 py-1 rounded border"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', textDecoration: 'none' }}
          >
            {label}
          </a>
        ))}
      </nav>

      <div className="space-y-8">
        <ListingsRefreshSection dashboard={dashboard} feedId={feed?.id} />

        <FolderConfigSection
          initialFoldersJson={dashboard?.folders_json}
          initialProcessedFolderId={dashboard?.processed_folder_id ?? null}
        />

        <EpgSettingsSection userId={user.id} feedId={feed?.id} />

        <StreamingSettingsSection sessionToken={session?.access_token} />

        <IngestHistorySection
          runs={ingestRuns}
          isLoading={ingestRunsLoading}
          isError={ingestRunsError}
          enabled={!!user && isAdmin}
        />

        <AdminSectionCard id="uploads" title="Accessibility uploads">
          <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Files arrive via Google Drive inbox folders (n8n). Deleting an upload here removes storage and overlay rows,
            then rebuilds TV accessibility flags automatically.
          </p>
          <BroadcasterAccessibilityUpload
            userId={user.id}
            showAllUploads
            hideUploadForm
            rebuildAfterDelete
          />
        </AdminSectionCard>
      </div>
    </div>
  )
}
